import { useState, useRef, useCallback } from 'react';
import { RefreshCw, AlertTriangle, CheckCircle2, X, Shield } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';

/* ── Node data per pipeline ──────────────────────────────────── */
const pipelines = {
  'contract-classification': {
    nodes: [
      { id: 'n1', col: 0, name: 'Contract Corpus 2024', status: 'verified', trust: 96, type: 'Data Source', lastVerified: 'Apr 14, 2026 · 09:00 UTC', flags: [], records: 23441 },
      { id: 'n2', col: 0, name: 'Classification Schema v9', status: 'verified', trust: 99, type: 'Data Source', lastVerified: 'Apr 14, 2026 · 09:00 UTC', flags: [], records: 23441 },
      { id: 'n3', col: 0, name: 'External Legal Corpus', status: 'unverified', trust: 41, type: 'Data Source', lastVerified: 'Unknown', flags: ['Provenance not established', 'No chain of custody documentation'], records: 8203 },
      { id: 'n4', col: 1, name: 'Fine-tune Pipeline', status: 'unverified', trust: 67, type: 'Processing Pipeline', lastVerified: 'Partial — Mar 28, 2026', flags: ['Unverified upstream dependency from External Legal Corpus'], records: 8203 },
      { id: 'n5', col: 2, name: 'GPT-4o 2025-11', status: 'indigo', trust: 88, type: 'AI Model', riskTier: 'HIGH', lastVerified: 'Apr 13, 2026', flags: [], records: 23441 },
      { id: 'n6', col: 3, name: 'Meridian API Gateway', status: 'verified', trust: 98, type: 'Infrastructure', lastVerified: 'Apr 15, 2026 · 14:30 UTC', flags: [], records: 47832 },
      { id: 'n7', col: 4, name: 'Classification App', status: 'verified', trust: 95, type: 'Application', lastVerified: 'Apr 14, 2026', flags: [], records: 23441 },
      { id: 'n8', col: 5, name: 'Analyst Dashboard', status: 'verified', trust: 100, type: 'Endpoint', lastVerified: 'Apr 15, 2026 · 14:32 UTC', humanReview: true, flags: [], records: 23441 },
    ],
    connections: [
      { from: 'n1', to: 'n4', type: 'normal', dataType: 'Training corpus data', volume: '23,441 documents' },
      { from: 'n2', to: 'n4', type: 'normal', dataType: 'Classification schema', volume: '1 schema definition' },
      { from: 'n3', to: 'n4', type: 'risk', dataType: 'Legal reference corpus', volume: '14 unverified documents' },
      { from: 'n4', to: 'n5', type: 'normal', dataType: 'Fine-tuned weights', volume: 'Model checkpoint' },
      { from: 'n5', to: 'n6', type: 'normal', dataType: 'API inference calls', volume: '~2,400 requests/day' },
      { from: 'n6', to: 'n7', type: 'normal', dataType: 'Routed responses', volume: '~2,400/day' },
      { from: 'n7', to: 'n8', type: 'normal', dataType: 'Classification results', volume: '~2,400 decisions/day' },
    ],
    summary: { verified: 5, total: 8, trust: 86, unverified: 2, lastMapped: '4 minutes ago' },
  },
  'legal-drafting': {
    nodes: [
      { id: 'n1', col: 0, name: 'Internal Legal Corpus', status: 'verified', trust: 94, type: 'Data Source', lastVerified: 'Apr 15, 2026 · 08:00 UTC', flags: [], records: 18720 },
      { id: 'n2', col: 0, name: 'Staff Identity Service', status: 'verified', trust: 100, type: 'Data Source', lastVerified: 'Apr 15, 2026 · 14:31 UTC', flags: [], records: 18720 },
      { id: 'n3', col: 0, name: 'Request Metadata DB', status: 'verified', trust: 97, type: 'Data Source', lastVerified: 'Apr 15, 2026 · 14:00 UTC', flags: [], records: 18720 },
      { id: 'n4', col: 1, name: 'RAG Retrieval Engine', status: 'verified', trust: 91, type: 'Processing Pipeline', lastVerified: 'Apr 14, 2026 · 18:00 UTC', flags: [], records: 18720 },
      { id: 'n5', col: 2, name: 'Claude Sonnet 3.7', status: 'indigo', trust: 82, type: 'AI Model', riskTier: 'HIGH', lastVerified: 'Apr 12, 2026', flags: [], records: 18720 },
      { id: 'n6', col: 3, name: 'Meridian API Gateway', status: 'verified', trust: 98, type: 'Infrastructure', lastVerified: 'Apr 15, 2026 · 14:30 UTC', flags: [], records: 47832 },
      { id: 'n7', col: 4, name: 'Legal Draft Portal', status: 'verified', trust: 93, type: 'Application', lastVerified: 'Apr 15, 2026 · 12:00 UTC', flags: [], records: 18720 },
      { id: 'n8', col: 5, name: 'Legal Reviewer UI', status: 'verified', trust: 100, type: 'Endpoint', lastVerified: 'Apr 15, 2026 · 14:31 UTC', humanReview: true, flags: [], records: 18720 },
    ],
    connections: [
      { from: 'n1', to: 'n4', type: 'normal', dataType: 'Legal precedent data', volume: '14,200 documents' },
      { from: 'n2', to: 'n4', type: 'normal', dataType: 'User identity context', volume: 'Per-request auth' },
      { from: 'n3', to: 'n4', type: 'normal', dataType: 'Request parameters', volume: '~600 requests/day' },
      { from: 'n4', to: 'n5', type: 'normal', dataType: 'Contextualized prompt', volume: '~600/day' },
      { from: 'n5', to: 'n6', type: 'normal', dataType: 'Draft recommendations', volume: '~600 drafts/day' },
      { from: 'n6', to: 'n7', type: 'normal', dataType: 'Routed drafts', volume: '~600/day' },
      { from: 'n7', to: 'n8', type: 'normal', dataType: 'Review-ready drafts', volume: '~600 decisions/day' },
    ],
    summary: { verified: 8, total: 8, trust: 94, unverified: 0, lastMapped: '2 minutes ago' },
  },
  'academy-recs': {
    nodes: [
      { id: 'n1', col: 0, name: 'Course Catalog', status: 'verified', trust: 100, type: 'Data Source', lastVerified: 'Apr 15, 2026 · 06:00 UTC', flags: [], records: 5680 },
      { id: 'n2', col: 0, name: 'User Interaction Logs', status: 'verified', trust: 95, type: 'Data Source', lastVerified: 'Apr 15, 2026 · 14:15 UTC', flags: [], records: 5680 },
      { id: 'n3', col: 0, name: 'External MOOC Feed', status: 'unverified', trust: 58, type: 'Data Source', lastVerified: 'Mar 30, 2026', flags: ['Third-party data feed — SLA not validated', 'Content quality metrics not tracked'], records: 1240 },
      { id: 'n4', col: 1, name: 'Feature Extraction', status: 'verified', trust: 89, type: 'Processing Pipeline', lastVerified: 'Apr 14, 2026 · 20:00 UTC', flags: [], records: 5680 },
      { id: 'n5', col: 2, name: 'Mistral 7B', status: 'indigo', trust: 77, type: 'AI Model', riskTier: 'MEDIUM', lastVerified: 'Apr 10, 2026', flags: [], records: 5680 },
      { id: 'n6', col: 3, name: 'Academy API Layer', status: 'verified', trust: 96, type: 'Infrastructure', lastVerified: 'Apr 15, 2026 · 14:10 UTC', flags: [], records: 5680 },
      { id: 'n7', col: 4, name: 'Recommendation Engine', status: 'verified', trust: 92, type: 'Application', lastVerified: 'Apr 14, 2026', flags: [], records: 5680 },
      { id: 'n8', col: 5, name: 'Learner Dashboard', status: 'verified', trust: 100, type: 'Endpoint', lastVerified: 'Apr 15, 2026 · 14:15 UTC', humanReview: false, flags: [], records: 5680 },
    ],
    connections: [
      { from: 'n1', to: 'n4', type: 'normal', dataType: 'Course metadata', volume: '2,847 courses' },
      { from: 'n2', to: 'n4', type: 'normal', dataType: 'User behavior signals', volume: '~12,000 events/day' },
      { from: 'n3', to: 'n4', type: 'risk', dataType: 'External course data', volume: '340 external courses' },
      { from: 'n4', to: 'n5', type: 'normal', dataType: 'Feature vectors', volume: '~1,800 requests/day' },
      { from: 'n5', to: 'n6', type: 'normal', dataType: 'Ranked recommendations', volume: '~1,800/day' },
      { from: 'n6', to: 'n7', type: 'normal', dataType: 'API responses', volume: '~1,800/day' },
      { from: 'n7', to: 'n8', type: 'normal', dataType: 'Personalized paths', volume: '~1,800 recs/day' },
    ],
    summary: { verified: 6, total: 8, trust: 88, unverified: 1, lastMapped: '8 minutes ago' },
  },
};

const colHeaders = ['DATA SOURCES', 'PROCESSING', 'MODEL', 'INFRASTRUCTURE', 'APPLICATION', 'ENDPOINT'];

const nodeColors = {
  verified: { border: '#10B981', bg: '#111827', text: '#10B981' },
  unverified: { border: '#F59E0B', bg: '#111827', text: '#F59E0B' },
  indigo: { border: '#6366F1', bg: '#111827', text: '#6366F1' },
};

/* ── Layout constants ───────────────────────────────────────── */
const SVG_W = 1100;
const SVG_H = 380;
const COL_W = SVG_W / 6;
const NODE_W = 140;
const NODE_H = 68;

function colX(col) { return COL_W * col + COL_W / 2; }

function calcPositions(nodeList) {
  const pos = {};
  const colNodes = {};
  nodeList.forEach(n => { if (!colNodes[n.col]) colNodes[n.col] = []; colNodes[n.col].push(n); });
  Object.entries(colNodes).forEach(([col, ns]) => {
    const c = parseInt(col);
    const totalH = ns.length * NODE_H + (ns.length - 1) * 20;
    const startY = (SVG_H - totalH) / 2;
    ns.forEach((n, i) => {
      pos[n.id] = { x: colX(c) - NODE_W / 2, y: startY + i * (NODE_H + 20), cx: colX(c), cy: startY + i * (NODE_H + 20) + NODE_H / 2 };
    });
  });
  return pos;
}

function bezierPath(fromId, toId, positions) {
  const from = positions[fromId];
  const to = positions[toId];
  const x1 = from.x + NODE_W + 4;
  const y1 = from.cy;
  const x2 = to.x - 4;
  const y2 = to.cy;
  const dx = (x2 - x1) * 0.45;
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
}

/* ── TrustGauge ─────────────────────────────────────────────── */
function TrustGauge({ score }) {
  const color = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : '#EF4444';
  return (
    <div className="flex items-center gap-2">
      <div className="w-20 h-2 rounded-full bg-[#1E2A45] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono font-bold" style={{ color }}>{score}/100</span>
    </div>
  );
}

/* ── Node Detail Panel ──────────────────────────────────────── */
function NodeDetail({ node, onClose }) {
  if (!node) return <div className="flex items-center justify-center h-full text-[#475569] text-sm p-6">Click a node to inspect</div>;
  const c = nodeColors[node.status];
  return (
    <ScrollArea className="h-full">
      <div className="p-5 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-sm font-semibold text-[#F8FAFC] mb-1">{node.name}</div>
            <div className="text-[10px] text-[#475569] uppercase tracking-wider">{node.type}</div>
          </div>
          <button onClick={onClose} className="text-[#475569] hover:text-[#F8FAFC]"><X size={16} /></button>
        </div>
        <div><div className="text-[10px] text-[#475569] uppercase mb-1">Verification</div><span className="text-xs font-medium" style={{ color: c.text }}>{node.status === 'indigo' ? 'Active · HIGH Risk' : node.status === 'verified' ? 'Verified' : 'Unverified'}</span></div>
        <div><div className="text-[10px] text-[#475569] uppercase mb-1">Trust Score</div><TrustGauge score={node.trust} /></div>
        <div><div className="text-[10px] text-[#475569] uppercase mb-1">Last Verified</div><div className="text-xs text-[#94A3B8] font-mono">{node.lastVerified}</div></div>
        {node.humanReview && <div className="p-2.5 rounded-lg bg-[#10B981]/5 border border-[#10B981]/20 text-xs text-[#10B981]"><CheckCircle2 size={12} className="inline mr-1" />Human review confirmed</div>}
        {node.riskTier && <div><div className="text-[10px] text-[#475569] uppercase mb-1">Risk Tier</div><span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/20">{node.riskTier}</span></div>}
        {node.flags.length > 0 && (
          <div>
            <div className="text-[10px] text-[#475569] uppercase mb-2">Risk Flags ({node.flags.length})</div>
            {node.flags.map((f, i) => (
              <div key={i} className="flex items-start gap-2 p-2.5 rounded-lg bg-[#F59E0B]/5 border border-[#F59E0B]/20 text-xs text-[#F59E0B] mb-1.5">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />{f}
              </div>
            ))}
          </div>
        )}
        <div><div className="text-[10px] text-[#475569] uppercase mb-1">Linked Records</div><div className="text-xs text-[#94A3B8] font-mono">{node.records?.toLocaleString()}</div></div>
      </div>
    </ScrollArea>
  );
}

/* ── SVG Flowing Particles ──────────────────────────────────── */
function FlowingParticles({ pathD, color, isRisk, pathId }) {
  const particleCount = isRisk ? 5 : 3;
  const dur = isRisk ? '2.5s' : '3.5s';
  return (
    <g>
      {Array.from({ length: particleCount }).map((_, i) => (
        <circle key={i} r={isRisk ? 3.5 : 2.5} fill={color} opacity={isRisk ? 0.9 : 0.7}>
          <animateMotion
            dur={dur}
            repeatCount="indefinite"
            begin={`${(i * (isRisk ? 0.5 : 1.2))}s`}
            path={pathD}
          />
        </circle>
      ))}
    </g>
  );
}

/* ── Connection Tooltip ─────────────────────────────────────── */
function ConnectionTooltip({ conn, x, y }) {
  if (!conn) return null;
  return (
    <g>
      <rect x={x - 100} y={y - 44} width={200} height={40} rx={8} fill="#0F1629" stroke="#1E2A45" strokeWidth={1} />
      <text x={x} y={y - 30} textAnchor="middle" fill="#F8FAFC" fontSize={11} fontWeight={500}>{conn.dataType}</text>
      <text x={x} y={y - 16} textAnchor="middle" fill="#64748B" fontSize={10}>{conn.volume}</text>
    </g>
  );
}

/* ── Node Tooltip ───────────────────────────────────────────── */
function NodeTooltip({ node, pos }) {
  if (!node || !pos) return null;
  const c = nodeColors[node.status];
  const tx = pos.cx;
  const ty = pos.y - 8;
  return (
    <g>
      <rect x={tx - 95} y={ty - 62} width={190} height={58} rx={8} fill="#0F1629" stroke={c.border} strokeWidth={1.5} />
      <text x={tx} y={ty - 44} textAnchor="middle" fill="#F8FAFC" fontSize={11} fontWeight={600}>{node.type}</text>
      <text x={tx} y={ty - 30} textAnchor="middle" fill={c.text} fontSize={10}>{node.status === 'verified' ? 'Verified' : node.status === 'unverified' ? 'Unverified' : 'Active'} · Trust {node.trust}/100</text>
      <text x={tx} y={ty - 16} textAnchor="middle" fill="#64748B" fontSize={9}>Last: {node.lastVerified?.substring(0, 20)}</text>
    </g>
  );
}

/* ── Main Component ─────────────────────────────────────────── */
export default function LineageGraph() {
  const [selectedNode, setSelectedNode] = useState(null);
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredConn, setHoveredConn] = useState(null);
  const [connTooltipPos, setConnTooltipPos] = useState({ x: 0, y: 0 });
  const [workflow, setWorkflow] = useState('contract-classification');
  const [highlightUnverified, setHighlightUnverified] = useState(false);
  const svgRef = useRef(null);

  const pipeline = pipelines[workflow] || pipelines['contract-classification'];
  const nodes = pipeline.nodes;
  const connections = pipeline.connections;
  const summary = pipeline.summary;
  const positions = calcPositions(nodes);

  const handleConnHover = useCallback((conn) => {
    const pos = calcPositions(pipelines[workflow]?.nodes || pipelines['contract-classification'].nodes);
    const from = pos[conn.from];
    const to = pos[conn.to];
    const mx = (from.x + NODE_W + to.x) / 2;
    const my = (from.cy + to.cy) / 2;
    setHoveredConn(conn);
    setConnTooltipPos({ x: mx, y: my });
  }, [workflow]);

  const handleUnverifiedClick = () => {
    setHighlightUnverified(true);
    setTimeout(() => setHighlightUnverified(false), 3000);
  };

  return (
    <div data-testid="lineage-graph" className="space-y-4">
      {/* Top bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <select data-testid="workflow-selector" value={workflow} onChange={(e) => { setWorkflow(e.target.value); setSelectedNode(null); setHoveredNode(null); setHoveredConn(null); }} className="px-3 py-2 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#F8FAFC] focus:outline-none focus:border-[#6366F1]">
          <option value="contract-classification">Contract Classification Pipeline</option>
          <option value="legal-drafting">Legal Document Drafting Pipeline</option>
          <option value="academy-recs">Academy Recommendations Pipeline</option>
        </select>
        <select className="px-3 py-2 bg-[#0F1629] border border-[#1E2A45] rounded-lg text-sm text-[#94A3B8] focus:outline-none focus:border-[#6366F1]">
          <option>All models</option>
          <option>GPT-4o only</option>
          <option>Claude Sonnet only</option>
        </select>
      </div>

      <div className={`grid grid-cols-1 ${selectedNode ? 'lg:grid-cols-4' : ''} gap-4`} style={{ minHeight: 420 }}>
        {/* Graph panel */}
        <div className={`${selectedNode ? 'lg:col-span-3' : ''} glass-card p-4 overflow-x-auto`}>
          <div style={{ minWidth: 1100 }}>
            {/* Column headers */}
            <div className="grid grid-cols-6 mb-1">
              {colHeaders.map((h) => (
                <div key={h} className="text-center text-[9px] font-semibold text-[#475569] uppercase tracking-[0.15em]">{h}</div>
              ))}
            </div>

            {/* SVG Graph */}
            <svg
              ref={svgRef}
              viewBox={`0 0 ${SVG_W} ${SVG_H}`}
              className="w-full"
              style={{ minHeight: 380 }}
            >
              <defs>
                {/* Arrowhead markers */}
                <marker id="arrow-indigo" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
                  <polygon points="0 0, 8 3, 0 6" fill="#6366F1" />
                </marker>
                <marker id="arrow-amber" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                  <polygon points="0 0, 10 3.5, 0 7" fill="#F59E0B" />
                </marker>
                {/* Glow filters */}
                <filter id="glow-green" x="-30%" y="-30%" width="160%" height="160%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <filter id="glow-amber" x="-30%" y="-30%" width="160%" height="160%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
                <filter id="glow-indigo" x="-30%" y="-30%" width="160%" height="160%">
                  <feGaussianBlur stdDeviation="3" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>

              {/* Connection paths */}
              {connections.map((conn) => {
                const pathD = bezierPath(conn.from, conn.to, positions);
                const isRisk = conn.type === 'risk';
                const color = isRisk ? '#F59E0B' : '#6366F1';
                const isHovered = hoveredConn === conn;
                return (
                  <g key={`${conn.from}-${conn.to}`}>
                    {/* Invisible wider hitbox for hover */}
                    <path
                      d={pathD}
                      fill="none"
                      stroke="transparent"
                      strokeWidth={20}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={() => handleConnHover(conn)}
                      onMouseLeave={() => setHoveredConn(null)}
                    />
                    {/* Visible path */}
                    <path
                      d={pathD}
                      fill="none"
                      stroke={color}
                      strokeWidth={isRisk ? 3 : 2}
                      strokeDasharray={isRisk ? '10 5' : 'none'}
                      markerEnd={isRisk ? 'url(#arrow-amber)' : 'url(#arrow-indigo)'}
                      opacity={isHovered ? 1 : (isRisk ? 0.85 : 0.6)}
                      filter={isRisk ? 'url(#glow-amber)' : 'none'}
                      style={{ transition: 'opacity 0.2s' }}
                    />
                    {/* Flowing particles */}
                    <FlowingParticles pathD={pathD} color={color} isRisk={isRisk} pathId={`${conn.from}-${conn.to}`} />
                  </g>
                );
              })}

              {/* Connection tooltip */}
              {hoveredConn && <ConnectionTooltip conn={hoveredConn} x={connTooltipPos.x} y={connTooltipPos.y} />}

              {/* Nodes */}
              {nodes.map((node) => {
                const pos = positions[node.id];
                const c = nodeColors[node.status];
                const isSelected = selectedNode?.id === node.id;
                const isHovered = hoveredNode?.id === node.id;
                const shouldPulse = highlightUnverified && (node.id === 'n3' || node.id === 'n4');
                return (
                  <g
                    key={node.id}
                    data-testid={`lineage-node-${node.id}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedNode(node)}
                    onMouseEnter={() => setHoveredNode(node)}
                    onMouseLeave={() => setHoveredNode(null)}
                  >
                    {/* Highlight pulse for unverified */}
                    {shouldPulse && (
                      <rect
                        x={pos.x - 6} y={pos.y - 6}
                        width={NODE_W + 12} height={NODE_H + 12}
                        rx={16} fill="none" stroke={c.border} strokeWidth={2}
                        opacity={0.6}
                      >
                        <animate attributeName="opacity" values="0.6;0.1;0.6" dur="1.5s" repeatCount="indefinite" />
                      </rect>
                    )}
                    {/* Selection glow */}
                    {isSelected && (
                      <rect
                        x={pos.x - 3} y={pos.y - 3}
                        width={NODE_W + 6} height={NODE_H + 6}
                        rx={14} fill="none" stroke={c.border} strokeWidth={2} opacity={0.5}
                        filter={node.status === 'verified' ? 'url(#glow-green)' : node.status === 'unverified' ? 'url(#glow-amber)' : 'url(#glow-indigo)'}
                      />
                    )}
                    {/* Node body */}
                    <rect
                      x={pos.x} y={pos.y}
                      width={NODE_W} height={NODE_H}
                      rx={12} fill={c.bg}
                      stroke={c.border}
                      strokeWidth={isHovered || isSelected ? 2.5 : 2}
                      style={{ transition: 'stroke-width 0.2s' }}
                    />
                    {/* Node name */}
                    {(() => {
                      const lines = node.name.length <= 16
                        ? [node.name]
                        : (() => { const w = node.name.split(' '); const m = Math.ceil(w.length / 2); return [w.slice(0, m).join(' '), w.slice(m).join(' ')]; })();
                      const baseY = node.riskTier ? (lines.length > 1 ? 18 : 22) : (lines.length > 1 ? 22 : 28);
                      return lines.map((line, li) => (
                        <text
                          key={li}
                          x={pos.cx} y={pos.y + baseY + li * 14}
                          textAnchor="middle"
                          fill="#F8FAFC"
                          fontSize={lines.length > 1 ? 11 : 12}
                          fontWeight={500}
                          fontFamily="'Inter', sans-serif"
                        >{line}</text>
                      ));
                    })()}
                    {/* Trust score */}
                    {(() => {
                      const isMulti = node.name.length > 16;
                      const trustY = node.riskTier ? (isMulti ? 48 : 38) : (isMulti ? 52 : 44);
                      return (
                        <text
                          x={pos.cx} y={pos.y + trustY}
                          textAnchor="middle"
                          fill={c.text}
                          fontSize={10}
                          fontFamily="'IBM Plex Mono', monospace"
                        >
                          Trust {node.trust}/100
                        </text>
                      );
                    })()}
                    {/* Warning icon for unverified */}
                    {node.status === 'unverified' && (
                      <text x={pos.cx} y={pos.y + 58} textAnchor="middle" fontSize={11} fill="#F59E0B">&#9888;</text>
                    )}
                    {/* HIGH risk badge */}
                    {node.riskTier && (
                      <g>
                        <rect x={pos.cx - 18} y={pos.y + 48} width={36} height={15} rx={7.5} fill="rgba(239,68,68,0.1)" stroke="rgba(239,68,68,0.2)" strokeWidth={1} />
                        <text x={pos.cx} y={pos.y + 59} textAnchor="middle" fill="#EF4444" fontSize={8} fontWeight={700} fontFamily="'IBM Plex Mono', monospace">{node.riskTier}</text>
                      </g>
                    )}
                  </g>
                );
              })}

              {/* Node hover tooltip */}
              {hoveredNode && !selectedNode && <NodeTooltip node={hoveredNode} pos={positions[hoveredNode.id]} />}
            </svg>
          </div>
        </div>

        {/* Detail panel */}
        {selectedNode && (
          <div className="glass-card overflow-hidden" data-testid="node-detail-panel">
            <NodeDetail node={selectedNode} onClose={() => setSelectedNode(null)} />
          </div>
        )}
      </div>

      {/* Provenance summary */}
      <div className="glass-card p-4 flex items-center justify-between flex-wrap gap-3" data-testid="provenance-summary">
        <div className="flex items-center gap-3 sm:gap-4 text-xs flex-wrap">
          <span className="text-[#F8FAFC]"><span className="text-[#10B981] font-bold">{summary.verified}</span>/{summary.total} sources verified</span>
          <span className="text-[#475569] hidden sm:inline">&middot;</span>
          <span className="text-[#F8FAFC]">Trust score <span className={`font-mono font-bold ${summary.trust >= 90 ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>{summary.trust}/100</span></span>
          {summary.unverified > 0 && (
            <>
              <span className="text-[#475569] hidden sm:inline">&middot;</span>
              <button onClick={handleUnverifiedClick} className="text-[#F59E0B] hover:text-[#FBBF24] transition-colors cursor-pointer underline decoration-dotted underline-offset-2">{summary.unverified} unverified upstream {summary.unverified === 1 ? 'dependency' : 'dependencies'}</button>
            </>
          )}
          <span className="text-[#475569] hidden sm:inline">&middot;</span>
          <span className="text-[#64748B]">Last mapped: {summary.lastMapped}</span>
        </div>
        <button data-testid="reverify-all" onClick={() => toast.info('Re-verification started for all sources')} className="flex items-center gap-2 bg-[#0F1629] border border-[#1E2A45] hover:border-[#6366F1] text-[#94A3B8] text-xs px-3 py-2 rounded-lg transition-colors flex-shrink-0">
          <RefreshCw size={12} /> Re-verify all sources
        </button>
      </div>
    </div>
  );
}
